import { ArrowUp } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import AIIcon from '@/components/Badge/AIBadge/AIIcon';
import { Flex, Space, VFlex } from '@/components/base';
import Spinner from '@/components/Common/Spinner';
import Transition from '@/components/Common/Transition';
import FlexibleTextarea from '@/components/Inputs/FlexibleTextarea';
import Typo from '@/components/Typography/Typo';
import {
  HEY_SYNCLY_ANSWER_STATUS,
  type HeySynclyChat,
} from '@/entities/hey-syncly';
import { GptActionButton } from '@/features/AskSyncly/components/commons';
import PromptArea from '@/features/AskSyncly/components/PromptArea';
import { CollapseState } from '@/features/AskSyncly/types';
import type { DashboardCardInfoT } from '@/features/Dashboard/types';
import { cn } from '@/lib/cn';
import { useMessage } from '@/messages';
import useHeySynclyStore from '@/stores/HeySynclyStore';
import { getAlphaColor } from '@/styles/colors/color.utils';
import { GPT_REQUEST_TYPE, type GptSocketBaseInfo } from '@/types/gptConst';
import { isEmptyString } from '@/utils/util';

type Props = {
  socketInfo: GptSocketBaseInfo;
  onSocketConnectChange: (task_id: string | null) => void;
  dashboardCardInfo?: DashboardCardInfoT;
  analysis_alias?: string;
  chat?: HeySynclyChat;
};

const AskSynclyPanel = (props: Props) => {
  const collapseTimer = useRef<NodeJS.Timeout | null>(null);
  const submit = useHeySynclyStore((state) => state.askSubmitByFeedbackList);
  const answer = useHeySynclyStore((state) => state.tempAnswer);
  const bottomAreaRef = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState('');
  const m = useMessage();

  const [panelState, setPanelState] = useState<CollapseState>(
    props.socketInfo.initialState || CollapseState.COLLAPSED,
  );
  const [bottomAreaHeight, setBottomAreaHeight] = useState(0);

  const isCollapsed =
    panelState === CollapseState.COLLAPSED ||
    panelState === CollapseState.COLLAPSING;

  const collapsePanel = () => {
    if (isCollapsed) return;
    if (collapseTimer.current) {
      clearTimeout(collapseTimer.current);
    }
    setPanelState(CollapseState.COLLAPSING);
    collapseTimer.current = setTimeout(() => {
      setPanelState(CollapseState.COLLAPSED);
    }, 300);
    if (bottomAreaRef.current) {
      setBottomAreaHeight(bottomAreaRef.current.clientHeight + 74);
    }
  };

  const expandPanel = () => {
    if (!isCollapsed) return;
    if (collapseTimer.current) {
      clearTimeout(collapseTimer.current);
    }
    setPanelState(CollapseState.EXPANDING);
    collapseTimer.current = setTimeout(() => {
      setPanelState(CollapseState.EXPANDED);
    }, 300);
  };

  const togglePanel = () => {
    if (isCollapsed) {
      expandPanel();
    } else {
      collapsePanel();
    }
  };

  const onGptActionClick = async (action: GPT_REQUEST_TYPE) => {
    switch (action) {
      case GPT_REQUEST_TYPE.ACTION:
        {
          const chat = await submit({
            ...props.socketInfo,
            question: m.heySyncly('GPT_TEMPLATE_ACTION') as string,
          });
          props.onSocketConnectChange(chat.alias);
        }

        break;
      case GPT_REQUEST_TYPE.SUMMARY:
        {
          const chat = await submit({
            ...props.socketInfo,
            question: m.heySyncly('GPT_TEMPLATE_SUMMARY') as string,
          });
          props.onSocketConnectChange(chat.alias);
        }
        break;
      default:
        throw new Error('Invalid action type');
    }
    expandPanel();
  };

  const inputSubmit = async () => {
    const val = inputValue;
    if (val.trim() === '') return;
    const chat = await submit({
      ...props.socketInfo,
      question: val,
    });
    setInputValue('');
    props.onSocketConnectChange(chat.alias);
    expandPanel();
  };

  useEffect(() => {
    return () => {
      props.onSocketConnectChange?.(null);
    };
  }, []);

  useEffect(() => {
    if (props.socketInfo.initialPrompt) {
      submit({
        ...props.socketInfo,
        question: props.socketInfo.initialPrompt,
      }).then((chat) => {
        props.onSocketConnectChange(chat.alias);
      });
    }
  }, [props.socketInfo.initialPrompt]);

  return (
    <AskSynclyAISection
      style={{
        height:
          panelState === CollapseState.COLLAPSING
            ? bottomAreaHeight
            : panelState === CollapseState.COLLAPSED
              ? 'auto'
              : 'calc(100vh - 160px)',
      }}
    >
      <VFlex f={1} style={{ overflow: 'auto' }}>
        <Flex j="center" width="100%">
          <Flex
            padding="16px"
            onClick={togglePanel}
            style={{ cursor: 'pointer' }}
          >
            <Handler />
          </Flex>
        </Flex>
        <Space h={8} />
        <Transition
          status={!isCollapsed}
          style={{
            flex: 1,
            overflow: 'auto',
            padding: '0px 24px',
            position: 'relative',
          }}
        >
          <GradientBar style={{ top: 0, zIndex: 1 }} />
          <PromptArea
            onGptActionClick={onGptActionClick}
            dashboardCardInfo={props.dashboardCardInfo}
            chart={props.socketInfo.chart}
            chat={props.chat}
          />
          <GradientBar
            style={{ bottom: 0, transform: 'rotate(180deg)', zIndex: 1 }}
          />
        </Transition>
      </VFlex>
      <BottomArea ref={bottomAreaRef}>
        <Transition status={isCollapsed || !!answer}>
          <Flex j="space-between" a="center">
            {(function () {
              switch (answer?.status) {
                case HEY_SYNCLY_ANSWER_STATUS.UNDERSTANDING:
                case HEY_SYNCLY_ANSWER_STATUS.ANALYZING:
                case HEY_SYNCLY_ANSWER_STATUS.REFINING:
                case HEY_SYNCLY_ANSWER_STATUS.REASONING:
                case HEY_SYNCLY_ANSWER_STATUS.GENERATING:
                  return (
                    <Flex a="center" g={8}>
                      <Spinner />
                      <Typo type="label2" weight="m" className="text-gray-600">
                        Generating
                      </Typo>
                    </Flex>
                  );
                case HEY_SYNCLY_ANSWER_STATUS.COMPLETE:
                  return (
                    <Flex a="center" g={8}>
                      <AIIcon className="text-2xl" />
                      <Typo type="label2" weight="m" className="text-green-700">
                        Answer generated
                      </Typo>
                    </Flex>
                  );
                default:
                  return (
                    <Flex a="center" g={8}>
                      <AIIcon className="text-2xl" />
                      <Typo type="label2" weight="m">
                        Ask any questions
                      </Typo>
                    </Flex>
                  );
              }
            })()}
            <Flex g={8}>
              <GptActionButton
                onClick={() => onGptActionClick(GPT_REQUEST_TYPE.ACTION)}
              >
                Suggest action
              </GptActionButton>
              <GptActionButton
                onClick={() => onGptActionClick(GPT_REQUEST_TYPE.SUMMARY)}
              >
                Summarize
              </GptActionButton>
            </Flex>
          </Flex>
        </Transition>
        <Space h={24} />
        <InputAreaStyle g={8} a="center">
          <FlexibleTextarea
            placeholder="Ask Syncly AI"
            style={{
              flex: 1,
              backgroundColor: 'transparent',
              border: 'none',
              height: 24,
              padding: 0,
              minHeight: 24,
              maxHeight: 150,
            }}
            rows={1}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                inputSubmit();
              }
            }}
          />
          <button
            className={cn(
              'absolute right-3 bottom-3 rounded-full bg-gray-900 size-9 flex items-center justify-center cursor-pointer transition-all duration-100 shadow hover:scale-105',
              isEmptyString(inputValue) && 'opacity-50 cursor-not-allowed',
            )}
            disabled={isEmptyString(inputValue)}
            onClick={inputSubmit}
          >
            <div className="transition-all duration-100 group-hover:scale-120">
              <ArrowUp className="size-4 text-white" />
            </div>
          </button>
        </InputAreaStyle>
      </BottomArea>
    </AskSynclyAISection>
  );
};

const AskSynclyAISection = styled(VFlex)`
  position: absolute;
  bottom: 0;
  width: calc(100% - 16px);
  border-radius: 24px;
  background-color: ${({ theme }) => theme.colors.gray200};
  transition: height 0.2s ease-in;
  margin: 8px;
`;

const Handler = styled(Flex)`
  width: 36px;
  height: 6px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.gray400};
  cursor: pointer;
`;

const BottomArea = styled(VFlex)`
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 0px 24px 24px;
  background-color: ${(props) => props.theme.colors.gray200};
  border-radius: 24px;
`;

const InputAreaStyle = styled(Flex)`
  position: relative;
  background-color: ${(props) => props.theme.colors.gray100};
  border-radius: 8px;
  border: 1px solid ${(props) => props.theme.colors.gray300};
  padding: 10px 64px 10px 16px;
  min-height: 60px;
`;

const GradientBar = styled.div`
  position: sticky;
  width: 100%;
  height: 24px;
  background: linear-gradient(
    to bottom,
    ${({ theme }) => theme.colors.gray200} 0%,
    ${({ theme }) => theme.colors.gray200} 25%,
    ${({ theme }) => getAlphaColor(theme.colors.gray200, 0)} 100%
  );
`;
export default AskSynclyPanel;
